import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Type,
  X,
  Minus,
  Plus,
  Palette
} from 'lucide-react';
import { FontSelector } from './FontSelector';
import { AdvancedColorPicker } from './AdvancedColorPicker';

export interface GlobalTypography {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  lineHeight: number;
  letterSpacing: number;
  color: string;
}

interface GlobalTypographyModalProps {
  isOpen: boolean;
  onClose: () => void;
  globalTypography: GlobalTypography;
  onChange: (updates: Partial<GlobalTypography>) => void;
}

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72, 96];

const FONT_WEIGHTS = [
  { value: '100', label: 'Thin' },
  { value: '200', label: 'Extra Light' },
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi Bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
  { value: '900', label: 'Black' }
];

export const GlobalTypographyModal: React.FC<GlobalTypographyModalProps> = ({
  isOpen,
  onClose,
  globalTypography,
  onChange
}) => {
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const updateTypography = useCallback((updates: Partial<GlobalTypography>) => {
    onChange(updates);
  }, [onChange]);

  const handleFontSizeChange = useCallback((delta: number) => {
    const currentSize = globalTypography?.fontSize || 16;
    const newSize = Math.max(8, Math.min(200, currentSize + delta));
    updateTypography({ fontSize: newSize });
  }, [globalTypography?.fontSize, updateTypography]);

  const handleLineHeightChange = useCallback((delta: number) => {
    const currentLineHeight = globalTypography?.lineHeight || 1.4;
    const newLineHeight = Math.max(0.8, Math.min(3, currentLineHeight + delta));
    updateTypography({ lineHeight: Math.round(newLineHeight * 10) / 10 });
  }, [globalTypography?.lineHeight, updateTypography]);

  const handleLetterSpacingChange = useCallback((delta: number) => {
    const currentSpacing = globalTypography?.letterSpacing || 0;
    const newSpacing = Math.max(-2, Math.min(10, currentSpacing + delta));
    updateTypography({ letterSpacing: Math.round(newSpacing * 10) / 10 });
  }, [globalTypography?.letterSpacing, updateTypography]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Type className="w-5 h-5 mr-2 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Global Typography</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Preview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-2">Preview</div>
              <div
                className="text-left"
                style={{
                  fontFamily: globalTypography?.fontFamily || 'Inter, sans-serif',
                  fontSize: `${globalTypography?.fontSize || 16}px`,
                  fontWeight: globalTypography?.fontWeight || '400',
                  lineHeight: globalTypography?.lineHeight || 1.4,
                  letterSpacing: `${globalTypography?.letterSpacing || 0}px`,
                  color: globalTypography?.color || '#000000'
                }}
              >
                The quick brown fox jumps over the lazy dog. This is how your global typography will look across all text elements in your template.
              </div>
            </div>

            {/* Font Family & Color */}
            <div className="grid grid-cols-2 gap-4">
              {/* Font Family */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                <button
                  onClick={() => setShowFontSelector(true)}
                  className="w-full p-3 text-left border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="text-sm truncate"
                      style={{ fontFamily: globalTypography?.fontFamily || 'Inter, sans-serif' }}
                    >
                      {(globalTypography?.fontFamily || 'Inter').split(',')[0]}
                    </span>
                    <Type className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <button
                  onClick={() => setShowColorPicker(true)}
                  className="w-full p-3 border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-5 h-5 rounded border border-gray-200 flex-shrink-0"
                      style={{ backgroundColor: globalTypography?.color || '#000000' }}
                    />
                    <span className="text-sm truncate">{globalTypography?.color || '#000000'}</span>
                    <Palette className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />
                  </div>
                </button>
              </div>
            </div>

            {/* Font Size & Weight */}
            <div className="grid grid-cols-2 gap-4">
              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleFontSizeChange(-1)}
                    className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <select
                    value={globalTypography?.fontSize || 16}
                    onChange={(e) => updateTypography({ fontSize: parseInt(e.target.value) })}
                    className="flex-1 p-2 border border-gray-300 rounded text-center"
                  >
                    {FONT_SIZES.map(size => (
                      <option key={size} value={size}>{size}px</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleFontSizeChange(1)}
                    className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Font Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Weight</label>
                <select
                  value={globalTypography?.fontWeight || '400'}
                  onChange={(e) => updateTypography({ fontWeight: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  {FONT_WEIGHTS.map(weight => (
                    <option key={weight.value} value={weight.value}>{weight.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Line Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Line Height: {globalTypography?.lineHeight || 1.4}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleLineHeightChange(-0.1)}
                  className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="range"
                  min="0.8"
                  max="3"
                  step="0.1"
                  value={globalTypography?.lineHeight || 1.4}
                  onChange={(e) => updateTypography({ lineHeight: parseFloat(e.target.value) })}
                  className="flex-1"
                />
                <button
                  onClick={() => handleLineHeightChange(0.1)}
                  className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Letter Spacing */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Letter Spacing: {globalTypography?.letterSpacing || 0}px
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleLetterSpacingChange(-0.1)}
                  className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="range"
                  min="-2"
                  max="10"
                  step="0.1"
                  value={globalTypography?.letterSpacing || 0}
                  onChange={(e) => updateTypography({ letterSpacing: parseFloat(e.target.value) })}
                  className="flex-1"
                />
                <button
                  onClick={() => handleLetterSpacingChange(0.1)}
                  className="p-2 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            These settings will apply to all new text elements. Existing elements can still be customized individually.
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
          >
            Done
          </button>
        </div>

        {/* Font Selector Modal */}
        {showFontSelector && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
            onClick={() => setShowFontSelector(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <FontSelector
                selectedFont={globalTypography?.fontFamily || 'Inter, sans-serif'}
                onFontSelect={(fontFamily) => {
                  updateTypography({ fontFamily });
                  setShowFontSelector(false);
                }}
                onClose={() => setShowFontSelector(false)}
                className="w-96 max-w-[90vw] max-h-[90vh]"
              />
            </div>
          </div>
        )}

        {/* Color Picker Modal */}
        {showColorPicker && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
            onClick={() => setShowColorPicker(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <AdvancedColorPicker
                color={globalTypography?.color || '#000000'}
                onChange={(color) => updateTypography({ color })}
                onClose={() => setShowColorPicker(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
